import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  EmptyState,
  ErrorState,
  FormActions,
  FormSection,
  Modal,
  Nota,
  PageHeader,
  Skeleton,
} from '../../../ui';
import StepperAdesao from '../../../components/adesao/StepperAdesao';

import { ContratoLista, abrirPdfContrato, assinarContrato } from '../../../api/contrato/contratoApi';
import { RootState, store } from '../../../store/store';
import { concluirRascunhoDoFluxo } from '../../../utils/adesaoFluxo';
import { rotuloStatusContrato } from '../../../utils/contratoStatus';
import {
  listarContratosDaAdesao,
  nomeDoContrato,
  resolverVeiculoDoFluxo,
} from '../../../utils/termosDaAdesao';

import { TERMO } from '../../termo/routes';
import { CONCLUIDO } from '../../concluido/routes';

/**
 * `/assinatura` — **etapa 6 dos TRÊS fluxos** (reescrita na at-60; em lote na at-81).
 *
 * ⚠️ Sem sobretítulo, pela mesma razão de `/produto` e `/termo`: os três fluxos
 * terminam aqui (`plan-c` §1.4).
 *
 * ## O que mudou na at-81 (feedback de campo, 03/11/2026)
 *
 *   1. 🔴 **Uma assinatura vale para TODOS os termos pendentes da adesão**
 *      (decisão de Victor). Cada contrato recebe a própria página de auditoria —
 *      com o título dele e o hash do PDF dele —, então a evidência continua
 *      sendo por documento.
 *   2. 🔴 **Os termos a assinar vêm do BANCO** (`utils/termosDaAdesao`), não de um
 *      `contratoId` no `location.state`. Isso fecha o defeito mais grave achado na
 *      análise: retomar um rascunho nesta etapa chegava sem `contratoId`, e a tela
 *      caía no caminho legado — um PDF público só com o rabisco —, deixando o
 *      contrato "Aguardando assinatura" enquanto o consultor achava que tinha
 *      assinado. **O caminho legado saiu desta tela.**
 *   3. **O associado lê antes de assinar.** Cada termo tem "Ler contrato", que abre
 *      a aba no próprio clique (o campo relatou que *"não aparece o termo"*).
 *   4. **Falha é dita por termo**, e "Assinar os restantes" reaproveita a mesma
 *      assinatura — não se pede ao associado para assinar de novo.
 *
 * ⚠️ **O que NÃO mudou:** a resolução do canvas (300×150 — é dela que sai o
 * base64 da assinatura) e o `concluirRascunhoDoFluxo` disparado **aqui**, quando
 * não sobra pendente, e não no botão "Concluir".
 */

type Estado = 'carregando' | 'ok' | 'erro';

const AssinaturaComponent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const voltar = useVoltar();

  const canvas = useRef<SignatureCanvas | null>(null);
  /** A assinatura capturada — guardada para "Assinar os restantes" sem pedir de novo. */
  const assinaturaCapturada = useRef<string | null>(null);

  const estadoDaRota = (location.state || {}) as { associadoId?: string; carroId?: string };
  const associadoDoFluxo = useSelector((state: RootState) => state.associado.associadoId);
  const placa = useSelector((state: RootState) => state.associado.carro?.placa);
  const associadoId = estadoDaRota.associadoId || associadoDoFluxo || null;

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');
  const [contratos, setContratos] = useState<ContratoLista[]>([]);
  const [falhas, setFalhas] = useState<Record<string, string>>({});

  const [modalAberto, setModalAberto] = useState(false);
  const [assinando, setAssinando] = useState(false);
  const [abrindoPdfDe, setAbrindoPdfDe] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    if (!associadoId) return;
    setEstado('carregando');
    try {
      const carroId =
        estadoDaRota.carroId || (await resolverVeiculoDoFluxo(associadoId, placa));
      if (!carroId) {
        setContratos([]);
        setEstado('ok');
        return;
      }
      setContratos(await listarContratosDaAdesao(carroId, associadoId));
      setEstado('ok');
    } catch (falha: any) {
      setErro(falha?.message || 'Não foi possível carregar os termos desta adesão.');
      setEstado('erro');
    }
    // `estadoDaRota.carroId` é estável durante a vida da tela.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associadoId, placa]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const pendentes = contratos.filter((c) => c.status === 'EMITIDO');
  const todosAssinados = contratos.length > 0 && pendentes.length === 0;

  // Rascunho (at-38): fim do fluxo quando não sobra pendente. Fica AQUI, e não no
  // botão "Concluir" — quem assina e fecha o app deixaria o rascunho em aberto.
  const concluiuRascunho = useRef(false);
  useEffect(() => {
    if (todosAssinados && !concluiuRascunho.current) {
      concluiuRascunho.current = true;
      void concluirRascunhoDoFluxo(dispatch, () => store.getState());
    }
  }, [todosAssinados, dispatch]);

  /** Assina os pendentes, um a um, com a mesma imagem. */
  const assinarPendentes = async (imagem: string) => {
    setAssinando(true);
    const novasFalhas: Record<string, string> = {};
    let assinados = 0;

    for (const contrato of pendentes) {
      try {
        await assinarContrato(contrato.id, imagem);
        assinados += 1;
      } catch (falha: any) {
        novasFalhas[contrato.id] = falha?.message ?? 'Não foi possível assinar este termo.';
      }
    }

    setFalhas(novasFalhas);
    setAssinando(false);
    setModalAberto(false);
    await carregar();

    if (Object.keys(novasFalhas).length === 0) {
      toast.success(assinados === 1 ? 'Contrato assinado com sucesso!' : 'Contratos assinados com sucesso!');
    } else {
      toast.warn('Parte dos termos não foi assinada — veja o motivo ao lado de cada um.');
    }
  };

  const salvarAssinatura = async () => {
    if (!canvas.current || canvas.current.isEmpty()) {
      toast.warn('Peça ao associado para assinar dentro da moldura.');
      return;
    }
    const imagem = canvas.current.toDataURL('image/png');
    assinaturaCapturada.current = imagem;
    await assinarPendentes(imagem);
  };

  /** Abre o PDF. ⚠️ Chamado DIRETO no clique: a aba precisa nascer no gesto. */
  const lerContrato = async (contratoId: string) => {
    setAbrindoPdfDe(contratoId);
    try {
      const abriuEmAba = await abrirPdfContrato(contratoId);
      if (!abriuEmAba) toast.info('O navegador bloqueou a aba nova — o termo foi baixado.');
    } catch (falha: any) {
      toast.error(falha?.message ?? 'Não foi possível abrir o contrato.');
    } finally {
      setAbrindoPdfDe(null);
    }
  };

  const temFalhas = Object.keys(falhas).length > 0;

  const conteudo = () => {
    if (!associadoId) {
      return (
        <EmptyState
          titulo="Nada para assinar nesta tela"
          descricao="A assinatura é aberta a partir do termo gerado. Volte à etapa do termo, gere os documentos e siga para a assinatura."
          icone="alerta"
          acao={
            <Botao variante="primario" onClick={() => navigate(TERMO())}>
              Ir para o termo
            </Botao>
          }
        />
      );
    }

    if (estado === 'carregando') return <Skeleton linhas={3} altura="tw-h-12" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar os termos desta adesão."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (contratos.length === 0) {
      return (
        <EmptyState
          titulo="Nenhum termo gerado para esta adesão"
          descricao="Gere os termos na etapa anterior antes de colher a assinatura."
          icone="sem-dados"
          acao={
            <Botao variante="primario" onClick={() => navigate(TERMO())}>
              Ir para o termo
            </Botao>
          }
        />
      );
    }

    return (
      <div className="tw-flex tw-flex-col tw-gap-6">
        <FormSection titulo="Termos desta adesão" icone="documento">
          <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
            Leia os termos com o associado antes de assinar. Uma assinatura vale para
            todos os termos pendentes, e cada um guarda a própria página de auditoria.
          </p>
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
            {contratos.map((contrato) => {
              const status = rotuloStatusContrato(contrato.status);
              const falha = falhas[contrato.id];
              return (
                <li
                  key={contrato.id}
                  className="tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-superficie-borda tw-p-3 sm:tw-flex-row sm:tw-items-center sm:tw-justify-between"
                >
                  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
                    <span className="tw-text-sm tw-font-semibold tw-text-texto-titulo">
                      {nomeDoContrato(contrato)}
                    </span>
                    <span>
                      <Chip variante={status.variante}>{status.texto}</Chip>
                    </span>
                    {falha && (
                      <span role="alert" className="tw-text-xs tw-text-danger">
                        Não foi assinado: {falha}
                      </span>
                    )}
                  </div>
                  <Botao
                    variante="secundario"
                    tamanho="sm"
                    carregando={abrindoPdfDe === contrato.id}
                    onClick={() => void lerContrato(contrato.id)}
                  >
                    {contrato.status === 'ASSINADO' ? 'Ver assinado' : 'Ler contrato'}
                  </Botao>
                </li>
              );
            })}
          </ul>
        </FormSection>

        {pendentes.length > 0 && (
          <FormSection titulo="Assinatura do associado" icone="pessoa">
            <p className="tw-m-0 tw-text-xs tw-text-texto-suave">
              {placa ? `Veículo ${placa}. ` : ''}O associado assina na tela do aparelho —{' '}
              {pendentes.length === 1
                ? '1 termo pendente.'
                : `${pendentes.length} termos pendentes.`}
            </p>
            {temFalhas && assinaturaCapturada.current ? (
              <Botao
                variante="primario"
                larguraTotal
                carregando={assinando}
                disabled={assinando}
                onClick={() => void assinarPendentes(assinaturaCapturada.current!)}
              >
                Assinar os restantes com a mesma assinatura
              </Botao>
            ) : (
              <Botao variante="secundario" larguraTotal onClick={() => setModalAberto(true)}>
                Abrir campo de assinatura
              </Botao>
            )}
          </FormSection>
        )}

        {todosAssinados && (
          <Nota>
            <strong>Todos os termos estão assinados.</strong> Cada documento assinado guarda
            o contrato inteiro mais a página de auditoria, com data, hora, IP e hash.
          </Nota>
        )}
      </div>
    );
  };

  return (
    <ShellSaac cabecalho={<PageHeader titulo="Assinatura" aoVoltar={voltar} />}>
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-3xl md:tw-p-6">
          <div className="tw-bg-white md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-border-b tw-border-superficie-borda">
              <StepperAdesao etapa={6} />
            </div>

            <div className="tw-p-4 md:tw-p-6">
              {conteudo()}

              {todosAssinados && (
                <FormActions>
                  <Botao variante="primario" larguraTotal onClick={() => navigate(CONCLUIDO())}>
                    Concluir
                  </Botao>
                </FormActions>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        aberto={modalAberto}
        titulo="Assinatura do associado"
        descricao={
          pendentes.length > 1
            ? `Assine dentro da moldura. A assinatura vale para os ${pendentes.length} termos pendentes.`
            : 'Assine dentro da moldura.'
        }
        ocupado={assinando}
        aoFechar={() => setModalAberto(false)}
        rodape={
          <>
            <Botao
              variante="primario"
              carregando={assinando}
              disabled={assinando}
              onClick={() => void salvarAssinatura()}
            >
              {assinando ? 'Assinando...' : 'Assinar'}
            </Botao>
            <Botao
              variante="secundario"
              disabled={assinando}
              onClick={() => canvas.current?.clear()}
            >
              Limpar
            </Botao>
          </>
        }
      >
        <div className="tw-flex tw-justify-center">
          <SignatureCanvas
            ref={canvas}
            penColor="black"
            /* ⚠️ 300×150 é a resolução do canvas, e é dela que sai o base64 que
               vai ao PDF e ao registro da assinatura — não alterar sem querer
               mudar a imagem gravada. */
            canvasProps={{
              style: {
                width: 300,
                height: 150,
                borderRadius: 16,
                touchAction: 'none',
                borderStyle: 'solid',
                borderWidth: 2,
                borderColor: '#E95E0C',
              },
            }}
          />
        </div>
      </Modal>
    </ShellSaac>
  );
};

export default AssinaturaComponent;
